import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useChild } from './ChildContext';
import websocketService from '../services/websocketService';
import realtimeSharingService from '../services/realtimeSharingService';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user, token, isAuthenticated } = useAuth();
  const { activeChild } = useChild();
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastEvent, setLastEvent] = useState(null);
  const [notifications, setNotifications] = useState([]);

  // Handle connection status changes
  const handleConnectionStatus = useCallback((status, _details) => {
    setConnectionStatus(status);
  }, []);

  // Handle incoming events
  const handleEvent = useCallback((eventType, data) => {
    setLastEvent({ type: eventType, data, timestamp: new Date() });

    // Handle different event types
    switch (eventType) {
      case 'child_event_created':
        // Trigger dashboard refresh or update specific components
        window.dispatchEvent(new CustomEvent('childEventCreated', { detail: data }));
        break;

      case 'notification':
        setNotifications(prev => [...prev, {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          ...data,
          timestamp: new Date()
        }]);
        break;

      case 'user_status':
        // Handle user online/offline status
        window.dispatchEvent(new CustomEvent('userStatusChanged', { detail: data }));
        break;

      case 'sync_conflict':
        // Handle sync conflicts
        window.dispatchEvent(new CustomEvent('syncConflict', { detail: data }));
        break;

      case 'queued_event':
        // Handle queued events from offline period
        window.dispatchEvent(new CustomEvent('queuedEvent', { detail: data }));
        break;

      default:
        // Generic event handler
        window.dispatchEvent(new CustomEvent('websocketEvent', {
          detail: { type: eventType, data }
        }));
    }
  }, []);

  // Connect/disconnect based on authentication
  useEffect(() => {
    if (isAuthenticated && token && user) {
      websocketService.connect(token);

      // Initialize real-time sharing service
      realtimeSharingService.initialize(websocketService);

      // Named handler wrappers so each registration can be removed precisely.
      const onChildEventCreated = (data) => handleEvent('child_event_created', data);
      const onNotification = (data) => handleEvent('notification', data);
      const onUserStatus = (data) => handleEvent('user_status', data);
      const onSyncConflict = (data) => handleEvent('sync_conflict', data);
      const onQueuedEvent = (data) => handleEvent('queued_event', data);
      const onError = (data) => handleEvent('error', data);
      const onSubscriptionSuccess = (data) => handleEvent('subscription_success', data);
      const onUnsubscriptionSuccess = (data) => handleEvent('unsubscription_success', data);
      const onEventMarkedRead = (data) => handleEvent('event_marked_read', data);
      const onSharingUpdate = (data) => handleEvent('sharing_update', data);
      const onInvitationUpdate = (data) => handleEvent('invitation_update', data);
      const onPermissionUpdate = (data) => handleEvent('permission_update', data);
      const onUserActivity = (data) => handleEvent('user_activity', data);
      const onShareNotification = (data) => handleEvent('share_notification', data);

      websocketService.onConnectionStatusChange(handleConnectionStatus);
      websocketService.on('child_event_created', onChildEventCreated);
      websocketService.on('notification', onNotification);
      websocketService.on('user_status', onUserStatus);
      websocketService.on('sync_conflict', onSyncConflict);
      websocketService.on('queued_event', onQueuedEvent);
      websocketService.on('error', onError);
      websocketService.on('subscription_success', onSubscriptionSuccess);
      websocketService.on('unsubscription_success', onUnsubscriptionSuccess);
      websocketService.on('event_marked_read', onEventMarkedRead);
      websocketService.on('sharing_update', onSharingUpdate);
      websocketService.on('invitation_update', onInvitationUpdate);
      websocketService.on('permission_update', onPermissionUpdate);
      websocketService.on('user_activity', onUserActivity);
      websocketService.on('share_notification', onShareNotification);

      return () => {
        websocketService.offConnectionStatusChange(handleConnectionStatus);
        websocketService.off('child_event_created', onChildEventCreated);
        websocketService.off('notification', onNotification);
        websocketService.off('user_status', onUserStatus);
        websocketService.off('sync_conflict', onSyncConflict);
        websocketService.off('queued_event', onQueuedEvent);
        websocketService.off('error', onError);
        websocketService.off('subscription_success', onSubscriptionSuccess);
        websocketService.off('unsubscription_success', onUnsubscriptionSuccess);
        websocketService.off('event_marked_read', onEventMarkedRead);
        websocketService.off('sharing_update', onSharingUpdate);
        websocketService.off('invitation_update', onInvitationUpdate);
        websocketService.off('permission_update', onPermissionUpdate);
        websocketService.off('user_activity', onUserActivity);
        websocketService.off('share_notification', onShareNotification);
        realtimeSharingService.cleanup();
      };
    }

    websocketService.disconnect();
    realtimeSharingService.cleanup();
    setConnectionStatus('disconnected');

    return () => {
      realtimeSharingService.cleanup();
    };
  }, [isAuthenticated, token, user, handleConnectionStatus, handleEvent]);

  // Subscribe to active child events
  useEffect(() => {
    if (connectionStatus === 'connected' && activeChild) {
      websocketService.subscribeToChild(activeChild.id);
    }
  }, [connectionStatus, activeChild]);

  // Utility functions
  const sendMessage = useCallback((message) => {
    websocketService.send(message);
  }, []);

  const subscribeToChild = useCallback((childId) => {
    websocketService.subscribeToChild(childId);
  }, []);

  const unsubscribeFromChild = useCallback((childId) => {
    websocketService.unsubscribeFromChild(childId);
  }, []);

  const markEventAsRead = useCallback((eventId) => {
    websocketService.markEventAsRead(eventId);
  }, []);

  const clearNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Real-time sharing methods
  const subscribeToSharingEvents = useCallback((eventType, callback) => {
    return realtimeSharingService.subscribe(eventType, callback)
  }, [])

  const getActiveUsers = useCallback((childId) => {
    return realtimeSharingService.getActiveUsers(childId)
  }, [])

  const startEditing = useCallback((childId, resourceType, resourceId) => {
    realtimeSharingService.startEditing(childId, resourceType, resourceId)
  }, [])

  const endEditing = useCallback((childId, resourceType, resourceId) => {
    realtimeSharingService.endEditing(childId, resourceType, resourceId)
  }, [])

  const value = {
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    lastEvent,
    notifications,
    sendMessage,
    subscribeToChild,
    unsubscribeFromChild,
    markEventAsRead,
    clearNotification,
    clearAllNotifications,
    // Real-time sharing methods
    subscribeToSharingEvents,
    getActiveUsers,
    startEditing,
    endEditing,
    realtimeSharingService,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};